import { useNavigate } from 'react-router-dom'
import { useModalContext } from '../../context/modal/modal'
import { RiDeleteBin6Line } from 'react-icons/ri'
import { BiEditAlt } from 'react-icons/bi'
import { CategoryTypes } from '../../interfaces/category'
import { useTranslation } from 'react-i18next'
import { BranchTypes } from '../../interfaces/organization'
import ModalConfirmationDialog from '../Modals/ModalConfirmationDialog'
import IconAction from '../UI/IconAction'
import useLimitedMode from '../../services/subscription/useLimitedMode'
import { useReportsContext } from '../../context/reports/reports'
import { countAttachedReports } from '../../helpers/attachedReports'

interface Props {
  data: CategoryTypes | BranchTypes
  type: 'category' | 'branch'
}

const CompanyBranchCategory = ({ data, type }: Props) => {
  const { t } = useTranslation()
  const { showConfirmationDialog, showConfirmationDialogHandler, resourceId } =
    useModalContext()
  const navigate = useNavigate()
  const { isLimited } = useLimitedMode()
  const { reports } = useReportsContext()

  const path = type === 'category' ? 'categories/edit-category' : 'branches/edit-branch'

  // Undefined while the report list is unknown, which counts as "do not offer
  // the delete" rather than "nothing is attached" - see `countAttachedReports`.
  const attachedReports = countAttachedReports(reports, type, data.id)
  const isDeletable = attachedReports === 0
  const deleteDisabledTitle = isLimited
    ? t('disabledUntilActivation')
    : attachedReports === undefined
      ? t('attachedReportsUnknown')
      : t(
          type === 'category'
            ? 'categoryHasAttachedReports'
            : 'branchHasAttachedReports',
          { count: attachedReports },
        )

  return (
    <>
      {/* One row owns the dialog: the one whose delete opened it. Rendering it
          on the bare `showConfirmationDialog` stacked an identical copy per
          category or branch on the page. */}
      {showConfirmationDialog && resourceId === data.id && (
        <ModalConfirmationDialog
          text={
            type === 'category'
              ? t('areYouSureWantToDeleteThisCategory')
              : t('areYouSureWantToDeleteThisBranch')
          }
          api={type === 'category' ? '/report/category' : '/organizations/branch'}
          toastSuccess={t(type === 'category' ? 'categoryHasBeenDeleted' : 'branchHasBeenDeleted')}
          toastError={t(type === 'category' ? 'failedToDeleteCategory' : 'failedToDeleteBranch')}
          swrKey={type === 'category' ? 'categories' : 'org'}
        />
      )}
      <div className='flex items-center justify-between px-5 xsm:px-8 border-[1px] border-solid rounded-lg border-borderGray text-lightGray text-base font-normal h-[5rem]'>
        <p>{data.title}</p>
        <div className='flex gap-5'>
          <IconAction
            icon={BiEditAlt}
            label={t('edit')}
            className='text-primaryBlue'
            disabled={isLimited}
            disabledTitle={t('disabledUntilActivation')}
            onClick={() => navigate(`/admin/${path}/${data.id}`)}
          />
          <IconAction
            icon={RiDeleteBin6Line}
            label={t('delete')}
            className='text-deleteRed'
            disabled={isLimited || !isDeletable}
            disabledTitle={deleteDisabledTitle}
            onClick={() => showConfirmationDialogHandler(data.id)}
          />
        </div>
      </div>
    </>
  )
}

export default CompanyBranchCategory
